import { useCallback, useEffect, useMemo, useState } from 'react'
import { neoSquareAuthApi } from '@/state/neo/neo-square-auth.api'

interface SquareLocation {
  id: string
  square_location_id: string
  location_name: string
  serial?: string | null
  integration_square_id?: string
}

/**
 * Every place that needs the full set of Square locations across every
 * integration on an org (venue switcher filter, mappings picker, etc.) used
 * to only fetch `integrations[0]` — so orgs with multiple Square accounts
 * saw a tiny subset. This hook + `<SquareLocationsFetcher />` helper pair
 * aggregates them all without breaking rules-of-hooks (we can't call
 * `useQuery` in a loop, so each fetcher is a child component that reports up).
 */
export function useAllSquareLocations(orgId: string, skip = false) {
  const { data: integrationsData, isLoading: integrationsLoading } =
    neoSquareAuthApi.useListSquareIntegrationsQuery(
      { organizationId: orgId },
      { skip: skip || !orgId }
    )
  const integrations = integrationsData?.integrations ?? []

  const [byIntegration, setByIntegration] = useState<
    Record<string, SquareLocation[]>
  >({})

  // Drop entries for integrations that are no longer present so stale serials
  // don't leak into the aggregate set forever.
  const integrationIdsKey = integrations.map((i: any) => i.id).join(',')
  useEffect(() => {
    setByIntegration((prev) => {
      const activeIds = new Set(integrations.map((i: any) => i.id))
      let changed = false
      const next: Record<string, SquareLocation[]> = {}
      for (const [id, locs] of Object.entries(prev)) {
        if (activeIds.has(id)) next[id] = locs
        else changed = true
      }
      return changed ? next : prev
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrationIdsKey])

  const report = useCallback(
    (integrationId: string, locations: SquareLocation[]) => {
      setByIntegration((prev) => {
        const existing = prev[integrationId]
        if (
          existing &&
          existing.length === locations.length &&
          existing.every(
            (l, idx) =>
              l.id === locations[idx]?.id && l.serial === locations[idx]?.serial
          )
        ) {
          return prev
        }
        return { ...prev, [integrationId]: locations }
      })
    },
    []
  )

  const allLocations = useMemo(
    () => Object.values(byIntegration).flat(),
    [byIntegration]
  )

  const mappedSerials = useMemo(() => {
    const set = new Set<string>()
    for (const loc of allLocations) {
      if (loc.serial) set.add(loc.serial)
    }
    return set
  }, [allLocations])

  return {
    integrations,
    integrationsLoading,
    allLocations,
    mappedSerials,
    report,
  }
}

export function SquareLocationsFetcher({
  integrationId,
  onLoad,
  skip = false,
}: {
  integrationId: string
  onLoad: (integrationId: string, locations: SquareLocation[]) => void
  skip?: boolean
}) {
  const { data } = neoSquareAuthApi.useGetSquareLocationsQuery(
    { integrationId },
    { skip: skip || !integrationId }
  )
  const locations = data?.locations ?? []
  const locationsKey = locations
    .map((l: any) => `${l.id}:${l.serial ?? ''}`)
    .join('|')
  useEffect(() => {
    onLoad(integrationId, locations)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrationId, locationsKey])
  return null
}
